import React, { memo, useState, useCallback, useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'
import { Tabs, Tab, LAYOUT } from 'transfix-ui/components/DeprecatedTabs'
import { InlineMessageProps, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Toast } from 'transfix-ui/components/Toast'
import { makeStyles } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton/Skeleton'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { NewDriverAutosuggest } from 'apps/fleet-planner/components/atoms/NewDriverAutosuggest'
import {
  ShareLoadForm,
  ShareLoadFormSchemaType,
} from 'apps/fleet-planner/components/share-load/ShareLoadForm'
import { ShareLoadTable } from 'apps/fleet-planner/components/share-load/ShareLoadTable'
import { ShareLoadContext } from 'apps/fleet-planner/context'
import { useDriverApi, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { ILoadDetailsState } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { fetchLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { unformatPhone } from 'utils/formatPhone'
import { ShareLoadAssignDriverCheckbox } from './ShareLoadAssignDriverCheckbox'
import { ShareLoadDriverInfo } from './ShareLoadDriverInfo'
import { ShareLoadTooltip } from './ShareLoadTooltip'
import 'assets/scss/5_components/_share_load_modal.scss'

interface IShareLoadModalProps {
  loadId?: string
  isOpen?: boolean
  onClose: (action?: string | undefined) => void
}

const useStyles = makeStyles({
  fixedHeight: {
    minHeight: '250px',
  },
  tab: {
    marginTop: '24px',
  },
  tabList: {
    width: '205px',
  },
})

type TTab = 'Driver 1' | 'Driver 2'

export const ShareLoadModal = memo<IShareLoadModalProps>(({ isOpen, loadId, onClose }) => {
  const [activeTab, setActiveTab] = useState<TTab>('Driver 1')
  const { trackEvent } = useFMSAnalytics()
  const classes = useStyles()
  const { load: shipment } = useSelector<IStore, ILoadDetailsState>(loadDetailsSelector)
  const isFmsOneTimeSmsLoadShareEnabled = useFeatureFlag('fmsOneTimeSmsLoadShare')
  const isFmsDriver2LoadShareEnabled = useFeatureFlag('fmsDriver2LoadShare')

  const { drivers } = shipment || {}
  const [assignedDriverOne, assignedDriverTwo] = drivers || []
  const { length: driversLength = 0 } = drivers || {}
  const isDriverAssigned = driversLength > 0

  const [autocompleteKey, setAutocompleteKey] = useState<number>(Date.now())
  const [selectedDriverOne, setSelectedDriverOne] = useState<Types.IDriver | undefined>({})
  const [selectedDriverTwo, setSelectedDriverTwo] = useState<Types.IDriver | undefined>({})
  const [isAssignDriverCheckboxChecked, setIsAssignDriverCheckboxChecked] = useState<boolean>(true)
  const dispatch = useDispatch()
  const { error: driverError, resetError: resetDriverError, updateDriver } = useDriverApi()

  const isDriverOneActive = activeTab === 'Driver 1'

  const tabSelectedDriver = isDriverOneActive ? selectedDriverOne : selectedDriverTwo
  const tabAssignedDriver = isDriverOneActive ? assignedDriverOne : assignedDriverTwo

  const { id: tabSelectedDriverId } = tabSelectedDriver || {}
  const { id: tabAssignedDriverId } = tabAssignedDriver || {}

  const isSelectedDriverAssigned = drivers?.some(d => d.id === tabSelectedDriverId)
  const isSelectedDriverAssignable = !!tabSelectedDriverId && !tabAssignedDriverId
  const isLoadAssignable =
    !isSelectedDriverAssigned && isSelectedDriverAssignable && isAssignDriverCheckboxChecked
  const currentSelectedDriver = isFmsDriver2LoadShareEnabled ? tabSelectedDriver : selectedDriverOne
  const { id: currentSelectedDriverId } = currentSelectedDriver || {}

  const {
    shipmentShares = [],
    createShare,
    sendShare,
    getAndSetShares,
    isLoading,
    error: apiError,
    resetError: resetShareError,
    pushNewToastMessage,
  } = useContext(ShareLoadContext)

  const toastMessage =
    isFmsOneTimeSmsLoadShareEnabled && isLoadAssignable
      ? 'Message sent and driver assigned.'
      : 'Message sent'

  /**
   * Find any previously created share for this shipment + driver
   */
  const getExistingShare = useCallback((shares?: Types.IShare[], driver?: Types.IDriver):
    | Types.IShare
    | undefined => {
    return shares?.find(s => `${s?.sharedWithDriver?.id}` === `${driver?.id}`)
  }, [])

  // Update only if data/state is out of sync
  useEffect(() => {
    if (loadId && loadId !== shipment.id) {
      dispatch(fetchLoadDetails(loadId))
    }
  }, [dispatch, loadId, shipment.id])

  // On mount, fetch all shares and set in context state
  useEffect(() => {
    if (shipment?.id && getAndSetShares) {
      trackEvent('Load', 'VIEWED_SHARE_LOAD_MODAL')
      getAndSetShares(shipment?.id)
    }
  }, [shipment?.id, getAndSetShares])

  const onChangeCheckboxHandler = () => {
    setIsAssignDriverCheckboxChecked(prev => !prev)
  }

  // Reset state, and initialize Autocomplete component
  const initAutocompleteInput = useCallback(() => {
    resetDriverError()
    resetShareError()
    if (isFmsDriver2LoadShareEnabled) {
      if (isDriverOneActive) {
        setSelectedDriverOne(undefined)
      } else {
        setSelectedDriverTwo(undefined)
      }
    } else {
      setSelectedDriverOne(undefined)
    }
    setAutocompleteKey(Date.now())
  }, [resetDriverError, resetShareError, activeTab])

  const cancelHandler = () => {
    onClose()
    initAutocompleteInput()
  }

  const setActiveTabHandler = (tab: TTab) => {
    setActiveTab(tab)
  }

  const memoShare = useCallback(() => {
    getExistingShare(shipmentShares, currentSelectedDriver)
  }, [currentSelectedDriver, getExistingShare, shipmentShares])

  const submitHandler = useCallback(
    async (data: ShareLoadFormSchemaType, shouldUpdateDriver: boolean) => {
      let hasValidCellphone = true
      const { id: driverId } = currentSelectedDriver || {}
      if (shouldUpdateDriver && driverId) {
        const { contactInformation } = data || {}
        const { cellPhone } = contactInformation || {}

        const { cellPhone: newPhone = '' } =
          (await updateDriver(driverId, {
            cellPhone: unformatPhone(cellPhone),
          })) || {}
        hasValidCellphone = !!newPhone
      }

      // If API request has failed, or no value is available
      if (!hasValidCellphone) return

      const existingShare = getExistingShare(shipmentShares, currentSelectedDriver)
      if (!existingShare?.id || !shipment?.id) return

      // Send share to driver
      try {
        const { notes: message = '' } = data || {}
        const shareSendRequest = {
          message,

          // This will change in later releases to have
          // more logic around text/email
          shareVia: [Types.ShareViaType.sms],
        }
        if (isFmsOneTimeSmsLoadShareEnabled) {
          const data = await sendShare(
            shipment.id,
            existingShare?.id,
            shareSendRequest,
            driverId,
            isLoadAssignable
          )

          if (data && loadId) {
            dispatch(fetchLoadDetails(loadId))
          }
        } else {
          await sendShare(shipment.id, existingShare?.id, shareSendRequest)
        }

        pushNewToastMessage(toastMessage)
        trackEvent('Load', 'SHARED_LOAD_WITH_DRIVER', {
          shipmentId: shipment.id,
          message,
          shareVia: [Types.ShareViaType.sms],
        })
      } catch (e) {
        // no op
      }

      // When finished, reset the form
      initAutocompleteInput()

      // Refresh the Autocomplete's informatino
      dispatch(fetchDrivers())
    },
    [
      getExistingShare,
      shipmentShares,
      tabSelectedDriver,
      shipment?.id,
      initAutocompleteInput,
      dispatch,
      updateDriver,
      sendShare,
      pushNewToastMessage,
      isAssignDriverCheckboxChecked,
      activeTab,
    ]
  )

  /**
   * When select driver input changes, create a share
   * and toggle selected driver
   */
  const driverOneChangeHandler = useCallback(
    async (driver?: Types.IDriver): Promise<void> => {
      setSelectedDriverOne(driver)

      // If it's undefined, don't update or do anything
      const existingShare = getExistingShare(shipmentShares, driver)
      if (!driver || !driver.id || existingShare || !shipment?.id) {
        return
      }

      await createShare(shipment?.id, driver.id)
    },
    [getExistingShare, shipmentShares, shipment?.id, createShare]
  )

  const driverTwoChangeHandler = useCallback(
    async (driver?: Types.IDriver): Promise<void> => {
      setSelectedDriverTwo(driver)

      // If it's undefined, don't update or do anything
      const existingShare = getExistingShare(shipmentShares, driver)
      if (!driver || !driver.id || existingShare || !shipment?.id) {
        return
      }

      await createShare(shipment?.id, driver.id)
    },
    [getExistingShare, shipmentShares, shipment?.id, createShare]
  )

  const filteredShares = orderBy(
    shipmentShares.filter(({ lastSharedAt }: Types.IShare): boolean => !!lastSharedAt),
    'lastSharedAt',
    'desc'
  )

  const isSharedWithSelectedDriver = filteredShares.some(
    share => share.sharedWithDriver.id === currentSelectedDriverId
  )

  /**
   * On mount default to the first driver assigned to load
   * and check for existing share or create a share
   */
  useEffect(() => {
    if (assignedDriverOne && isEmpty(selectedDriverOne) && selectedDriverOne !== undefined) {
      driverOneChangeHandler(assignedDriverOne)
    }

    if (
      isFmsDriver2LoadShareEnabled &&
      assignedDriverTwo &&
      isEmpty(selectedDriverTwo) &&
      selectedDriverTwo !== undefined
    ) {
      driverTwoChangeHandler(assignedDriverTwo)
    }
  }, [
    assignedDriverOne,
    driverOneChangeHandler,
    selectedDriverOne,
    assignedDriverTwo,
    activeTab,
    selectedDriverTwo,
    driverTwoChangeHandler,
  ])

  const existingShare = getExistingShare(shipmentShares, selectedDriverOne)

  const errorMsg = driverError || apiError

  const inlineMessage: InlineMessageProps | undefined = errorMsg
    ? {
        message: errorMsg,
        type: INLINE_MESSAGE_VARIANTS.ALERT,
      }
    : undefined

  const [isToastOpen, setIsToastOpen] = useState<boolean>(!isDriverAssigned)
  const closeToastHandler = () => setIsToastOpen(false)

  const sectionClassName = currentSelectedDriverId ? '' : classes.fixedHeight

  return (
    <ModalBase
      modalType={MODAL_TYPES.form}
      className='fp-share-load-modal'
      closeOnBackdropClick
      onClose={onClose}
      isClose
      size={8}
      inlineMessageProps={inlineMessage}
      title={isFmsOneTimeSmsLoadShareEnabled ? 'Share Load via SMS' : 'Share Load'}
      open={isOpen}>
      {isFmsOneTimeSmsLoadShareEnabled && <div className='share-load-modal-separator' />}
      <section className={`${sectionClassName} mb4`}>
        <div className='row '>
          <div className='col-lg-9 col-md-12'>
            <div className='share-load-search'>
              <div className='share-load-search-input'>
                {isFmsOneTimeSmsLoadShareEnabled && isFmsDriver2LoadShareEnabled && (
                  <Tabs
                    value={activeTab}
                    onChange={setActiveTabHandler}
                    tabListClassName={classes.tabList}
                    layout={LAYOUT.fixed}
                    tabPanelClassName={classes.tab}>
                    <Tab label='Driver 1'>
                      <ShareLoadDriverInfo
                        selectedDriver={selectedDriverOne}
                        isSharedWithSelectedDriver={isSharedWithSelectedDriver}
                        assignedDriver={assignedDriverOne}
                        shipment={shipment}
                      />
                    </Tab>
                    <Tab label='Driver 2'>
                      <ShareLoadDriverInfo
                        selectedDriver={selectedDriverTwo}
                        isSharedWithSelectedDriver={isSharedWithSelectedDriver}
                        assignedDriver={assignedDriverTwo}
                        shipment={shipment}
                      />
                    </Tab>
                  </Tabs>
                )}
                {!isFmsOneTimeSmsLoadShareEnabled && !isFmsDriver2LoadShareEnabled && (
                  <ShareLoadDriverInfo
                    selectedDriver={selectedDriverOne}
                    isSharedWithSelectedDriver={isSharedWithSelectedDriver}
                    assignedDriver={assignedDriverOne}
                    shipment={shipment}
                  />
                )}

                <NewDriverAutosuggest
                  disabled={isLoading || (!isFmsOneTimeSmsLoadShareEnabled && !isDriverAssigned)}
                  value={tabSelectedDriver}
                  key={autocompleteKey}
                  onChange={isDriverOneActive ? driverOneChangeHandler : driverTwoChangeHandler}
                  dataTest='share-load-driver-autocomplete'
                  large
                />

                {isFmsOneTimeSmsLoadShareEnabled && isFmsDriver2LoadShareEnabled && (
                  <ShareLoadAssignDriverCheckbox
                    selectedDriver={tabSelectedDriver}
                    assignedDriver={isDriverOneActive ? assignedDriverOne : assignedDriverTwo}
                    isLoading={isLoading}
                    onChangeCheckbox={onChangeCheckboxHandler}
                    isAssignDriverCheckboxChecked={isAssignDriverCheckboxChecked}
                  />
                )}
                {!isFmsOneTimeSmsLoadShareEnabled && (
                  <Toast
                    open={isToastOpen}
                    onClose={closeToastHandler}
                    variation='alert'
                    message='Please assign a driver to this load before sharing.'
                  />
                )}
              </div>
              {!isFmsOneTimeSmsLoadShareEnabled && <ShareLoadTooltip />}
            </div>
          </div>
        </div>
      </section>

      {isFmsOneTimeSmsLoadShareEnabled ? (
        <section>
          <ShareLoadForm
            selectedDriver={currentSelectedDriver?.id ? currentSelectedDriver : {}}
            share={existingShare}
            shipment={shipment}
            onSubmit={submitHandler}
            onCancel={cancelHandler}
            isLoading={isLoading}
            isLoadAssignable={isLoadAssignable}
            drivers={drivers}
            isSelectedDriverAssigned={isSelectedDriverAssigned}
          />
        </section>
      ) : (
        <>
          {selectedDriverOne && selectedDriverOne?.id && (
            <section>
              <ShareLoadForm
                selectedDriver={selectedDriverOne}
                share={existingShare}
                shipment={shipment}
                onSubmit={submitHandler}
                onCancel={cancelHandler}
                isLoading={isLoading}
              />
            </section>
          )}
          <div className='share-load-modal-separator' />
          <section className='mb5'>
            {shipmentShares.length || memoShare.length ? (
              <ShareLoadTable shares={shipmentShares || memoShare.length} />
            ) : (
              <>{isLoading && <Skeleton height={300} />}</>
            )}
          </section>
        </>
      )}
    </ModalBase>
  )
})
